import React, { useEffect, useMemo, useState } from 'react';

import { ApartmentOutlined } from '@ant-design/icons';
import {
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { ActiveShift, AdminUniversity, AdminUserDetail } from 'models/Lead';

import { LeadService } from 'services/LeadService';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import appI18n from '../../../../config/i18n';
import { OTHER_UNIVERSITY_VALUE } from '../leadHelpers';
import SectionActions from './SectionActions';
import SectionEditFooter from './SectionEditFooter';

interface Props {
  detail: AdminUserDetail;
  universities: AdminUniversity[];
  universitiesLoading: boolean;
  shifts: ActiveShift[];
  shiftsLoading: boolean;
  onUpdated: (next: AdminUserDetail) => void;
}

const buildValues = (detail: AdminUserDetail) => {
  const usingOther =
    !!detail.profile.otherUniversity && !detail.profile.universityId;
  return {
    studyChoice:
      detail.profile.notStudying === true ? 'not_studying' : 'studying',
    universityId: usingOther
      ? OTHER_UNIVERSITY_VALUE
      : detail.profile.universityId || undefined,
    otherUniversity: detail.profile.otherUniversity || '',
    career: detail.profile.career || '',
    shiftIds: (detail.availabilityShifts || []).map((s) => s.id),
  };
};

function OtherSection({
  detail,
  universities,
  universitiesLoading,
  shifts,
  shiftsLoading,
  onUpdated,
}: Props) {
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const watchedStudyChoice = Form.useWatch('studyChoice', form);
  const watchedUniversityId = Form.useWatch('universityId', form);
  const isOtherSelectedEdit = watchedUniversityId === OTHER_UNIVERSITY_VALUE;
  const isNotStudyingEdit = watchedStudyChoice === 'not_studying';

  const isNotStudyingView = detail.profile.notStudying === true;
  const isOtherSelectedView =
    !!detail.profile.otherUniversity && !detail.profile.universityId;

  const isNotStudying = editing ? isNotStudyingEdit : isNotStudyingView;
  const isOtherSelected = editing ? isOtherSelectedEdit : isOtherSelectedView;

  useEffect(() => {
    form.setFieldsValue(buildValues(detail));
  }, [detail, form]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    if (saving) return;
    form.setFieldsValue(buildValues(detail));
    setEditing(false);
  };

  const universityOptions = useMemo(() => {
    const opts: { value: number; label: string }[] = universities.map((u) => ({
      value: u.id,
      label: u.name,
    }));
    opts.push({
      value: OTHER_UNIVERSITY_VALUE,
      label: t(
        'scenes.leads.components.detail_lead_modal.sections.other.options.other'
      ),
    });
    return opts;
  }, [universities, t]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const isNotStudyingNow = values.studyChoice === 'not_studying';
      let payload: any = {
        notStudying: isNotStudyingNow,
        shiftIds: values.shiftIds || [],
      };

      if (isNotStudyingNow) {
        payload = {
          ...payload,
          universityId: null,
          otherUniversity: null,
          career: null,
        };
      } else if (values.universityId === OTHER_UNIVERSITY_VALUE) {
        payload = {
          ...payload,
          universityId: null,
          otherUniversity: values.otherUniversity || null,
          career: values.career || null,
        };
      } else if (values.universityId) {
        payload = {
          ...payload,
          universityId: values.universityId,
          otherUniversity: null,
          career: values.career || null,
        };
      } else {
        payload = {
          ...payload,
          universityId: null,
          otherUniversity: null,
          career: null,
        };
      }

      const response = await LeadService.updateProfileOther(
        detail.user.id,
        payload
      );

      if (response.success) {
        message.success(
          t(
            'scenes.leads.components.detail_lead_modal.sections.other.messages.update_success'
          )
        );
        const refreshed = await LeadService.getById(detail.user.id);
        if (refreshed.success) {
          onUpdated(refreshed.data as AdminUserDetail);
        }
        setEditing(false);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.leads.components.detail_lead_modal.sections.other.messages.update_failed'
            ),
          3.5
        );
      }
    } catch (e) {
      // validation error
    } finally {
      setSaving(false);
    }
  };

  const formatAvailabilityDate = (iso: string): string => {
    const datePart = FunctionsHelper.getDateFormatByLang(i18n.language);
    return dayjs(iso).format(`${datePart} h:mm A`);
  };

  const fieldsDisabled = !editing || saving;

  return (
    <Card
      className={
        editing
          ? 'lead-section-card lead-section-card--editing'
          : 'lead-section-card'
      }
      size="small"
      title={
        <Space size={8}>
          <ApartmentOutlined />
          <span>
            {t(
              'scenes.leads.components.detail_lead_modal.sections.other.title'
            )}
          </span>
        </Space>
      }
      extra={<SectionActions editing={editing} onEdit={handleEdit} />}
    >
      <Form form={form} layout="vertical" disabled={!editing}>
        <Form.Item
          name="studyChoice"
          label={t(
            'scenes.leads.components.detail_lead_modal.sections.other.labels.study_status'
          )}
        >
          <Radio.Group disabled={fieldsDisabled}>
            <Radio value="studying">
              {t(
                'scenes.leads.components.detail_lead_modal.sections.other.options.studying'
              )}
            </Radio>
            <Radio value="not_studying">
              {t(
                'scenes.leads.components.detail_lead_modal.sections.other.options.not_studying'
              )}
            </Radio>
          </Radio.Group>
        </Form.Item>

        {!isNotStudying && (
          <>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="universityId"
                  label={t(
                    'scenes.leads.components.detail_lead_modal.sections.other.labels.university'
                  )}
                >
                  <Select
                    showSearch
                    allowClear
                    loading={universitiesLoading}
                    disabled={fieldsDisabled || universitiesLoading}
                    options={universityOptions}
                    optionFilterProp="label"
                    placeholder={t(
                      'scenes.leads.components.detail_lead_modal.sections.other.placeholders.university'
                    )}
                  />
                </Form.Item>
              </Col>
              {isOtherSelected && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="otherUniversity"
                    label={t(
                      'scenes.leads.components.detail_lead_modal.sections.other.labels.other_university'
                    )}
                    rules={[
                      {
                        required: editing,
                        message: t(
                          'scenes.leads.components.detail_lead_modal.sections.other.validations.other_university_required'
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'scenes.leads.components.detail_lead_modal.sections.other.placeholders.other_university'
                      )}
                      disabled={fieldsDisabled}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item
              name="career"
              label={t(
                'scenes.leads.components.detail_lead_modal.sections.other.labels.career'
              )}
              dependencies={['universityId']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, rawValue) {
                    const careerValue = (rawValue as string) || '';
                    const uni = getFieldValue('universityId');
                    if (uni && careerValue.trim().length === 0) {
                      return Promise.reject(
                        new Error(
                          t(
                            'scenes.leads.components.detail_lead_modal.sections.other.validations.career_required_with_university'
                          )
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input
                placeholder={t(
                  'scenes.leads.components.detail_lead_modal.sections.other.placeholders.career'
                )}
                disabled={fieldsDisabled}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="shiftIds"
          label={
            <Space direction="vertical" size={0}>
              <span>
                {t(
                  'scenes.leads.components.detail_lead_modal.sections.other.labels.availability_shifts'
                )}
              </span>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, fontWeight: 'normal' }}
              >
                {t(
                  'scenes.leads.components.detail_lead_modal.sections.other.labels.availability_last_updated'
                )}
                :{' '}
                {detail.profile.availabilityLastUpdatedAt
                  ? formatAvailabilityDate(
                      detail.profile.availabilityLastUpdatedAt
                    )
                  : t(
                      'scenes.leads.components.detail_lead_modal.sections.other.labels.availability_never_updated'
                    )}
              </Typography.Text>
            </Space>
          }
        >
          <Checkbox.Group disabled={fieldsDisabled || shiftsLoading}>
            <Space wrap>
              {shifts.map((s) => (
                <Checkbox key={s.id} value={s.id}>
                  {s.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>

        {editing && (
          <SectionEditFooter
            saving={saving}
            onCancel={handleCancel}
            onSave={handleSave}
          />
        )}
      </Form>
    </Card>
  );
}

export default OtherSection;
